import React, { useState, useMemo } from 'react';
import { useAuth } from './AuthContext';
import {
    Container,
    Box,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Collapse,
    Tooltip
} from '@mui/material';
import {
    PercentOutlined as PercentIcon,
    ShowChart as ChartIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Star,
    StarBorder
} from '@mui/icons-material';
import { useFavoriteMetrics } from '../hooks/useFavoriteMetrics';
import { useTheme } from '@mui/material/styles';
import TimeRangeButtons from './TimeRangeButtons';
import { formatLargeNumber } from './StockChart/utils/formatters';
import FinancialChartModal from './FinancialChartModal';

const CompanyIncomeStatement = ({ data: incomeStatementData, growthData: incomeStatementGrowthData }) => {
    const { user } = useAuth();
    const theme = useTheme();
    const [timeRange, setTimeRange] = useState('5Y');
    const [periodType, setPeriodType] = useState('annual');
    const [expandedRows, setExpandedRows] = useState({});
    const [chartModal, setChartModal] = useState({ open: false, metric: null });

    const processedData = useMemo(() => {
        if (!incomeStatementData?.annual || !incomeStatementData?.quarterly) return null;
        return {
            annual: incomeStatementData.annual,
            quarterly: incomeStatementData.quarterly
        };
    }, [incomeStatementData]);

    const processedGrowthData = useMemo(() => {
        if (!incomeStatementGrowthData?.annual || !incomeStatementGrowthData?.quarterly) return null;
        return {
            annual: incomeStatementGrowthData.annual,
            quarterly: incomeStatementGrowthData.quarterly
        };
    }, [incomeStatementGrowthData]);

    const currentData = useMemo(() => {
        if (!processedData) return [];
        if (periodType === 'annual') {
            return processedData.annual;
        } else {
            return Object.values(processedData.quarterly)
                .flat()
                .sort((a, b) => new Date(a.date) - new Date(b.date));
        }
    }, [processedData, periodType]);

    const currentGrowthData = useMemo(() => {
        if (!processedGrowthData) return [];
        if (periodType === 'annual') {
            return processedGrowthData.annual;
        } else {
            return Object.values(processedGrowthData.quarterly)
                .flat()
                .sort((a, b) => new Date(a.date) - new Date(b.date));
        }
    }, [processedGrowthData, periodType]);

    const getFilteredData = (data) => {
        if (!data || !data.length) return [];
        const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));
        if (timeRange === 'MAX') {
            return sortedData;
        }
        const yearLimit = {
            '1Y': 1,
            '3Y': 3,
            '5Y': 5,
            '10Y': 10
        }[timeRange];
        return sortedData.slice(0, yearLimit);
    };

    const filteredData = getFilteredData(currentData);

    const filteredGrowthData = useMemo(() => {
        if (!currentGrowthData.length || !filteredData.length) return [];
        const growthMap = new Map();
        currentGrowthData.forEach(growth => {
            const year = growth.fiscalYear;
            const period = growth.period || 'FY';
            const key = `${period}-${year}`;
            growthMap.set(key, growth);
        });
        return filteredData.map(statement => {
            const year = statement.fiscalYear;
            const period = statement.period || 'FY';
            const key = `${period}-${year}`;
            return growthMap.get(key) || null;
        });
    }, [currentGrowthData, filteredData]);

    const toggleRowExpansion = (rowKey) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowKey]: !prev[rowKey]
        }));
    };

    // Define which metrics get which chart type
    const getChartType = (metricKey) => {
        const tier1 = ['revenue', 'netIncome', 'operatingIncome', 'ebitda', 'eps', 'epsDiluted'];
        const tier2 = ['grossProfit', 'operatingExpenses', 'researchAndDevelopmentExpenses', 'costOfRevenue'];

        if (tier1.includes(metricKey)) return 'combo'; // Bar + Line
        if (tier2.includes(metricKey)) return 'line';  // Line only
        return null; // No chart
    };

    const handleChartClick = (metric) => {
        setChartModal({ open: true, metric });
    };

    const handleCloseChart = () => {
        setChartModal({ open: false, metric: null });
    };

    const formatValue = (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'number') {
            return formatLargeNumber(value);
        }
        return value;
    };

    const formatGrowthValue = (value) => {
        if (value === null || value === undefined) return '-';
        const percentage = (value * 100);
        const formatted = percentage.toFixed(2);
        return `${formatted}%`;
    };

    const getGrowthColor = (value) => {
        if (value === null || value === undefined) return theme.palette.text.secondary;
        return value >= 0 ? theme.palette.positive.main : theme.palette.negative.main;
    };

    const metrics = [
        // Tier 1: Most Important - Revenue & Profitability
        { key: 'revenue', label: 'Revenue', growthKey: 'growthRevenue' },
        { key: 'grossProfit', label: 'Gross Profit', growthKey: 'growthGrossProfit' },
        { key: 'operatingIncome', label: 'Operating Income', growthKey: 'growthOperatingIncome' },
        { key: 'ebitda', label: 'EBITDA', growthKey: 'growthEBITDA' },
        { key: 'netIncome', label: 'Net Income', growthKey: 'growthNetIncome' },
        { key: 'eps', label: 'EPS', growthKey: 'growthEPS' },
        { key: 'epsDiluted', label: 'EPS Diluted', growthKey: 'growthEPSDiluted' },

        // Tier 2: Cost Structure
        { key: 'costOfRevenue', label: 'Cost of Revenue', growthKey: 'growthCostOfRevenue' },
        { key: 'operatingExpenses', label: 'Operating Expenses', growthKey: 'growthOperatingExpenses' },
        { key: 'researchAndDevelopmentExpenses', label: 'R&D Expenses', growthKey: 'growthResearchAndDevelopmentExpenses' },
        { key: 'generalAndAdministrativeExpenses', label: 'G&A Expenses', growthKey: 'growthGeneralAndAdministrativeExpenses' },
        { key: 'sellingAndMarketingExpenses', label: 'Selling & Marketing Expenses', growthKey: 'growthSellingAndMarketingExpenses' },

        // Tier 3: Tax & Other
        { key: 'incomeBeforeTax', label: 'Income Before Tax', growthKey: 'growthIncomeBeforeTax' },
        { key: 'incomeTaxExpense', label: 'Income Tax Expense', growthKey: 'growthIncomeTaxExpense' },
        { key: 'depreciationAndAmortization', label: 'Depreciation & Amortization', growthKey: 'growthDepreciationAndAmortization' },
        { key: 'interestIncome', label: 'Interest Income', growthKey: 'growthInterestIncome' },
        { key: 'interestExpense', label: 'Interest Expense', growthKey: 'growthInterestExpense' },

        // Tier 4: Share Information
        { key: 'weightedAverageShsOut', label: 'Weighted Avg Shares', growthKey: 'growthWeightedAverageShsOut' },
        { key: 'weightedAverageShsOutDil', label: 'Weighted Avg Shares (Diluted)', growthKey: 'growthWeightedAverageShsOutDil' }
    ];

    // Use favorite metrics hook
    const { sortedMetrics, toggleFavorite, isFavorite } = useFavoriteMetrics('income', metrics);

    if (!processedData) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <Typography variant="h6" color="textSecondary">
                        No income statement data available
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container
            maxWidth="xl"
            sx={{ px: { xs: 0, sm: 2, md: 3 } }}
        >
            <Box my={4}>
                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mb: 3, px: { xs: 2, sm: 0 } }}
                >
                    <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ mb: 2, width: '100%' }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Period Type</InputLabel>
                                <Select
                                    value={periodType}
                                    label="Period Type"
                                    onChange={(e) => setPeriodType(e.target.value)}
                                >
                                    <MenuItem value="annual">Annual</MenuItem>
                                    <MenuItem value="quarter">Quarterly</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <TimeRangeButtons
                            periodType={periodType}
                            timeRange={timeRange}
                            setTimeRange={setTimeRange}
                            userPlan={user.plan.name}
                            size="medium"
                        />
                    </Grid>
                </Grid>

                <TableContainer
                    component={Paper}
                    variant="stats"
                    elevation={3}
                    sx={{
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        '& .MuiTable-root': {
                            minWidth: { xs: '800px', sm: 'auto' }
                        }
                    }}
                >
                    <Table sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 'bold',
                                        width: { xs: '180px', sm: '250px' },
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        py: { xs: 0.75, sm: 1.5 },
                                        px: { xs: 1, sm: 2 }
                                    }}
                                >
                                    Metric
                                </TableCell>
                                {filteredData.map((statement, index) => (
                                    <TableCell
                                        key={index}
                                        align="right"
                                        sx={{
                                            fontWeight: 'bold',
                                            width: { xs: '100px', sm: '150px' },
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                            py: { xs: 0.75, sm: 1.5 },
                                            px: { xs: 1, sm: 2 }
                                        }}
                                    >
                                        {periodType === 'annual'
                                            ? `FY ${statement.fiscalYear || new Date(statement.date).getFullYear()}`
                                            : `${statement.period} ${statement.fiscalYear || new Date(statement.date).getFullYear()}`
                                        }
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedMetrics.map((metric) => {
                                const chartType = getChartType(metric.key);
                                return (
                                    <React.Fragment key={metric.key}>
                                        <TableRow
                                            hover
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(0, 0, 0, 0.02)'
                                                }
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    width: { xs: '180px', sm: '250px' },
                                                    py: { xs: 0.75, sm: 1.5 },
                                                    px: { xs: 1, sm: 2 }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                                                    {/* Star Icon for Favorites */}
                                                    <Tooltip title={isFavorite(metric.key) ? "Remove from favorites" : "Add to favorites"}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleFavorite(metric.key)}
                                                            sx={{
                                                                color: isFavorite(metric.key)
                                                                    ? theme.palette.warning.main
                                                                    : theme.palette.text.disabled,
                                                                p: { xs: 0.25, sm: 0.5 }
                                                            }}
                                                        >
                                                            {isFavorite(metric.key) ?
                                                                <Star fontSize="small" /> :
                                                                <StarBorder fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Toggle Growth">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleRowExpansion(metric.key)}
                                                            sx={{
                                                                color: expandedRows[metric.key]
                                                                    ? theme.palette.primary.main
                                                                    : theme.palette.text.secondary,
                                                                p: { xs: 0.25, sm: 0.5 }
                                                            }}
                                                        >
                                                            {expandedRows[metric.key] ?
                                                                <ExpandLessIcon fontSize="small" /> :
                                                                <ExpandMoreIcon fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </Tooltip>

                                                    {chartType && (
                                                        <Tooltip title="View Chart">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleChartClick(metric)}
                                                                sx={{
                                                                    color: theme.palette.text.secondary,
                                                                    p: { xs: 0.25, sm: 0.5 }
                                                                }}
                                                            >
                                                                <ChartIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                        }}
                                                    >
                                                        {metric.label}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {filteredData.map((statement, index) => (
                                                <TableCell
                                                    key={index}
                                                    align="right"
                                                    sx={{
                                                        width: { xs: '100px', sm: '150px' },
                                                        py: { xs: 0.75, sm: 1.5 },
                                                        px: { xs: 1, sm: 2 }
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                                                    >
                                                        {formatValue(statement[metric.key])}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>

                                        <TableRow>
                                            <TableCell
                                                colSpan={filteredData.length + 1}
                                                sx={{ p: 0, border: 0 }}
                                            >
                                                <Collapse in={expandedRows[metric.key]} timeout="auto" unmountOnExit>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: theme.palette.mode === 'dark'
                                                                ? 'rgba(255, 255, 255, 0.03)'
                                                                : 'rgba(0, 0, 0, 0.02)',
                                                            borderLeft: `3px solid ${theme.palette.primary.main}`
                                                        }}
                                                    >
                                                        <Table sx={{ tableLayout: 'fixed' }}>
                                                            <TableBody>
                                                                <TableRow>
                                                                    <TableCell
                                                                        sx={{
                                                                            width: { xs: '180px', sm: '250px' },
                                                                            pl: { xs: 4, sm: 8 },
                                                                            py: { xs: 0.75, sm: 1.5 },
                                                                            px: { xs: 1, sm: 2 },
                                                                            border: 0
                                                                        }}
                                                                    >
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                                                                            <PercentIcon
                                                                                fontSize="small"
                                                                                sx={{
                                                                                    color: theme.palette.text.secondary,
                                                                                    fontSize: { xs: '0.9rem', sm: '1.25rem' }
                                                                                }}
                                                                            />
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontStyle: 'italic',
                                                                                    color: theme.palette.text.secondary,
                                                                                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                                                                                }}
                                                                            >
                                                                                {metric.label} Growth (YoY)
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    {filteredGrowthData.map((growthStatement, index) => (
                                                                        <TableCell
                                                                            key={index}
                                                                            align="right"
                                                                            sx={{
                                                                                width: { xs: '100px', sm: '150px' },
                                                                                py: { xs: 0.75, sm: 1.5 },
                                                                                px: { xs: 1, sm: 2 },
                                                                                border: 0
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    color: growthStatement
                                                                                        ? getGrowthColor(growthStatement[metric.growthKey])
                                                                                        : theme.palette.text.secondary,
                                                                                    fontWeight: 600,
                                                                                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                                                                                }}
                                                                            >
                                                                                {growthStatement
                                                                                    ? formatGrowthValue(growthStatement[metric.growthKey])
                                                                                    : '-'
                                                                                }
                                                                            </Typography>
                                                                        </TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Chart Modal */}
            {chartModal.open && chartModal.metric && (
                <FinancialChartModal
                    open={chartModal.open}
                    onClose={handleCloseChart}
                    metricLabel={chartModal.metric.label}
                    chartType={getChartType(chartModal.metric.key)}
                    filteredData={filteredData}
                    filteredGrowthData={filteredGrowthData}
                    metricKey={chartModal.metric.key}
                    growthKey={chartModal.metric.growthKey}
                    periodType={periodType}
                />
            )}
        </Container>
    );
};

export default CompanyIncomeStatement;